import { useEffect, useRef, useState } from 'react';
import {
  CONSENT_EVENT,
  getConsentState,
  grantAllConsent,
  rejectNonessentialConsent,
  updateConsent,
  type ConsentState,
} from '../lib/consent';

export function PrivacyChoices({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [state, setState] = useState<ConsentState>(() => getConsentState());
  const [managing, setManaging] = useState(false);
  const [analytics, setAnalytics] = useState(state.analytics_storage === 'granted');
  const [advertising, setAdvertising] = useState(state.ad_storage === 'granted');
  const headingRef = useRef<HTMLHeadingElement>(null);
  const dialogRef = useRef<HTMLElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    const listener = (event: Event) => setState((event as CustomEvent<ConsentState>).detail);
    window.addEventListener(CONSENT_EVENT, listener);
    return () => window.removeEventListener(CONSENT_EVENT, listener);
  }, []);

  useEffect(() => {
    if (!open) return;
    previousFocusRef.current = document.activeElement as HTMLElement | null;
    setAnalytics(state.analytics_storage === 'granted');
    setAdvertising(state.ad_storage === 'granted');
    headingRef.current?.focus();
    const handleKeyboard = (event: KeyboardEvent) => {
      if (event.key !== 'Tab' || !dialogRef.current) return;
      const controls = dialogRef.current.querySelectorAll<HTMLElement>(
        'button:not([disabled]), input:not([disabled]), a[href]',
      );
      const first = controls[0];
      const last = controls[controls.length - 1];
      if (!first || !last) return;
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    window.addEventListener('keydown', handleKeyboard);
    return () => {
      window.removeEventListener('keydown', handleKeyboard);
      if (previousFocusRef.current?.isConnected) previousFocusRef.current.focus();
    };
  }, [open, state]);

  if (!open) return null;
  const decide = (action: () => void) => {
    action();
    setManaging(false);
    onClose();
  };
  return (
    <div
      className="fixed inset-0 z-[80] flex items-end bg-navy-950/55 p-3 sm:items-center sm:justify-center"
      role="presentation"
    >
      <section
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="privacy-choices-title"
        className="max-h-[calc(100dvh-1.5rem)] w-full max-w-2xl overflow-y-auto rounded-2xl bg-white p-5 shadow-2xl sm:p-7"
      >
        <h2
          id="privacy-choices-title"
          ref={headingRef}
          tabIndex={-1}
          className="text-2xl font-black text-navy-950"
        >
          Privacy choices
        </h2>
        <p className="mt-3 text-sm leading-6 text-slate-600">
          SolarGabay uses browser storage for your local plan. With your permission, Google
          Analytics measures general site use and Monetag may serve and measure ads. Calculator
          inputs, bills, quotations, contacts, and saved plans are never included in analytics or
          advertising events.
        </p>
        {managing && (
          <div className="mt-5 grid gap-3">
            <label className="flex min-h-14 items-center justify-between gap-4 rounded-xl border border-slate-200 p-4">
              <span>
                <strong className="block text-navy-950">Analytics storage</strong>
                <span className="text-xs text-slate-600">
                  Helps measure pages and coarse interactions.
                </span>
              </span>
              <input
                type="checkbox"
                className="h-6 w-6"
                checked={analytics}
                onChange={(event) => setAnalytics(event.target.checked)}
              />
            </label>
            <label className="flex min-h-14 items-center justify-between gap-4 rounded-xl border border-slate-200 p-4">
              <span>
                <strong className="block text-navy-950">
                  Advertising storage and personalization
                </strong>
                <span className="text-xs text-slate-600">
                  Allows ad measurement and personalized ads where permitted.
                </span>
              </span>
              <input
                type="checkbox"
                className="h-6 w-6"
                checked={advertising}
                onChange={(event) => setAdvertising(event.target.checked)}
              />
            </label>
          </div>
        )}
        <div className="mt-6 grid gap-3 sm:grid-cols-3">
          <button type="button" className="btn-primary" onClick={() => decide(grantAllConsent)}>
            Accept all
          </button>
          <button
            type="button"
            className="btn-secondary"
            onClick={() => decide(rejectNonessentialConsent)}
          >
            Reject nonessential
          </button>
          {managing ? (
            <button
              type="button"
              className="btn-secondary"
              onClick={() =>
                decide(() =>
                  updateConsent(
                    {
                      analytics_storage: analytics ? 'granted' : 'denied',
                      ad_storage: advertising ? 'granted' : 'denied',
                      ad_user_data: advertising ? 'granted' : 'denied',
                      ad_personalization: advertising ? 'granted' : 'denied',
                    },
                    'custom',
                  ),
                )
              }
            >
              Save preferences
            </button>
          ) : (
            <button type="button" className="btn-secondary" onClick={() => setManaging(true)}>
              Manage preferences
            </button>
          )}
        </div>
      </section>
    </div>
  );
}
